import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollText, MessageCircle } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CtaButton {
  text: string;
  href: string;
  icon?: React.ReactNode;
}

export interface CtaFinalProps {
  /** Petite étiquette au-dessus du titre */
  label?: string;
  /** Titre principal */
  title?: string;
  /** Description sous le titre */
  description?: string;
  /** Bouton principal (gauche) */
  primaryButton?: CtaButton;
  /** Bouton secondaire (droite) */
  secondaryButton?: CtaButton;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_LABEL = "Prêt à commencer ?";
const DEFAULT_TITLE =
  "Votre projet d'études à l'étranger mérite le meilleur accompagnement";
const DEFAULT_DESCRIPTION =
  "Rejoignez les 250+ étudiants qui ont fait confiance à MomsEduc Study pour réaliser leur rêve académique.";

const DEFAULT_PRIMARY: CtaButton = {
  text: "Demander une AVI",
  href: "/contact",
  icon: <ScrollText size={18} />,
};

const DEFAULT_SECONDARY: CtaButton = {
  text: "Parler à un conseiller",
  href: "/contact",
  icon: <MessageCircle size={18} />,
};

// ─── Animation variants ───────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  ${tw`py-24 text-center px-6`}
  background: #ff6c00;
  margin-left: -2rem;
  margin-right: -2rem;
`;

const Inner = tw.div`max-w-3xl mx-auto`;

const Label = tw.p`text-sm font-bold uppercase tracking-widest text-white opacity-75 mb-4`;

const Title = tw.h2`text-3xl md:text-4xl font-black text-white mb-5 leading-tight`;

const Description = tw.p`text-white opacity-75 max-w-xl mx-auto mb-12 leading-relaxed`;

const ButtonsRow = tw.div`flex flex-col sm:flex-row gap-4 justify-center`;

const PrimaryBtn = styled(Link)`
  ${tw`px-8 py-4 font-black no-underline transition-all duration-300 inline-flex items-center justify-center gap-2`}
  background: #111111;
  color: #ffffff;
  &:hover {
    background: #f7fafc;
    color: #ff6c00;
  }
`;

const SecondaryBtn = styled(Link)`
  ${tw`px-8 py-4 font-black border-2 border-white no-underline transition-all duration-300 inline-flex items-center justify-center gap-2`}
  background: transparent;
  color: #ffffff;
  &:hover {
    background: #ffffff;
    color: #ff6c00;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CtaFinal: React.FC<CtaFinalProps> = ({
  label = DEFAULT_LABEL,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  primaryButton = DEFAULT_PRIMARY,
  secondaryButton = DEFAULT_SECONDARY,
}) => (
  <Wrapper>
    <Inner>
      <MDiv
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <MDiv variants={itemVariants}>
          <Label>{label}</Label>
        </MDiv>

        <MDiv variants={itemVariants}>
          <Title>{title}</Title>
        </MDiv>

        <MDiv variants={itemVariants}>
          <Description>{description}</Description>
        </MDiv>

        <MDiv variants={itemVariants}>
          <ButtonsRow>
            <PrimaryBtn to={primaryButton.href}>
              {primaryButton.icon}
              {primaryButton.text}
            </PrimaryBtn>
            <SecondaryBtn to={secondaryButton.href}>
              {secondaryButton.icon}
              {secondaryButton.text}
            </SecondaryBtn>
          </ButtonsRow>
        </MDiv>
      </MDiv>
    </Inner>
  </Wrapper>
);

export default CtaFinal;
